// apps/web/src/components/primitives/isian/dom.ts
/**
 * Reading a form from the page, for the form wrapper (FormIsian): the fields with a problem
 * (every element that says aria-invalid="true", with its label and its sentence, in page
 * order), the stops Enter walks (a radio group is one stop), and moving the focus to a field.
 * The summary itself (`[data-ringkasan]`) is never read as a field. A field inside
 * `[data-ringkasan-baris="n"]` (a member card) is named with its row.
 */
import { format, uiStrings } from '@satu/shared'
import type { KeyboardEvent } from 'react'
import type { SasaranEnter } from './enter'
import type { KontrolGalat } from './ringkasan'

const teksRingkasan = uiStrings.isian.ringkasan
const CALON = 'input, select, textarea, [role="radio"], [role="checkbox"]'
const INPUT_LEWAT = new Set(['hidden', 'submit', 'button', 'reset', 'image', 'file'])

let nomorId = 0

/** The element's id, given one when it has none (a summary link points at it). */
export function pastikanId(el: HTMLElement): string {
  if (el.id === '') {
    nomorId += 1
    el.id = `isian-${nomorId}`
  }
  return el.id
}

function teksDariId(el: HTMLElement, ids: string | null): string {
  const doc = el.ownerDocument
  return (ids ?? '')
    .split(/\s+/)
    .filter((id) => id !== '')
    .map((id) => doc.getElementById(id)?.textContent?.trim() ?? '')
    .filter((t) => t !== '')
    .join(' ')
}

function labelSaja(el: HTMLElement): string {
  const berlabel =
    el instanceof HTMLInputElement ||
    el instanceof HTMLSelectElement ||
    el instanceof HTMLTextAreaElement
  const label = berlabel ? el.labels?.[0]?.textContent?.trim() : undefined
  if (label !== undefined && label !== '') return label
  const lewatId = teksDariId(el, el.getAttribute('aria-labelledby'))
  return lewatId !== '' ? lewatId : (el.getAttribute('aria-label') ?? '')
}

/**
 * The field's label; inside a row of repeated fields (`data-ringkasan-baris`, a member card
 * of the KK grid) with the row, "NIK (baris 2)", as the grid's own cells name themselves.
 */
function labelDari(el: HTMLElement): string {
  const label = labelSaja(el)
  const baris = el.closest('[data-ringkasan-baris]')?.getAttribute('data-ringkasan-baris')
  return baris === null || baris === undefined || baris === '' || label === ''
    ? label
    : format(teksRingkasan.baris, { label, baris })
}

function pesanDari(el: HTMLElement): string | null {
  const ids = (el.getAttribute('aria-describedby') ?? '').split(/\s+/)
  const galat = ids.find((id) => id.endsWith('-error') || id.endsWith('-pesan'))
  if (galat === undefined) return null
  const t = el.ownerDocument.getElementById(galat)?.textContent?.trim() ?? ''
  return t === '' ? null : t
}

/**
 * The fields with a problem, in page order; each names the element its summary line moves the
 * focus to by id (a radio group's first radio), found again when the line is used.
 */
export function kumpulkanGalat(akar: HTMLElement): KontrolGalat[] {
  const out: KontrolGalat[] = []
  for (const el of akar.querySelectorAll<HTMLElement>('[aria-invalid="true"]')) {
    if (el.closest('[data-ringkasan]') !== null) continue
    const sasaran =
      el.getAttribute('role') === 'radiogroup'
        ? (el.querySelector<HTMLElement>('[role="radio"]') ?? el)
        : el
    out.push({ kunci: pastikanId(sasaran), label: labelDari(el), pesan: pesanDari(el) })
  }
  return out
}

function bisaDiisi(el: HTMLElement): boolean {
  if (el.closest('[data-ringkasan]') !== null || el.closest('[inert]') !== null) return false
  // Hidden itself or inside a hidden part (a collapsed block, a region hidden from readers).
  if (el.closest('[hidden], [aria-hidden="true"]') !== null) return false
  if ((el as HTMLInputElement).disabled === true) return false
  // Locked, itself or as a whole group (a field its "Tidak tercatat" tick locked, its buttons).
  if (el.closest('[aria-disabled="true"]') !== null || el.hasAttribute('data-disabled'))
    return false
  // A control beside a field, not a field of its own (the "Tidak tercatat" tick).
  if (el.hasAttribute('data-lewati-enter')) return false
  if (el instanceof HTMLInputElement && INPUT_LEWAT.has(el.type)) return false
  return el.getAttribute('role') === 'radio' || el.tabIndex >= 0
}

export function stopIsian(akar: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = []
  const grup = new Set<Element>()
  for (const el of akar.querySelectorAll<HTMLElement>(CALON)) {
    if (!bisaDiisi(el)) continue
    const g = el.getAttribute('role') === 'radio' ? el.closest('[role="radiogroup"]') : null
    if (g !== null) {
      if (grup.has(g)) continue
      grup.add(g)
    }
    out.push(el)
  }
  return out
}

export function indeksStop(stop: readonly HTMLElement[], el: HTMLElement): number {
  const i = stop.indexOf(el)
  if (i >= 0) return i
  const g = el.closest('[role="radiogroup"]')
  return g === null ? -1 : stop.findIndex((s) => g.contains(s))
}

export function fokusKontrol(el: HTMLElement | undefined): void {
  if (el === undefined) return
  const g = el.getAttribute('role') === 'radio' ? el.closest('[role="radiogroup"]') : null
  const sasaran = g?.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]') ?? el
  sasaran.focus()
  if (typeof sasaran.scrollIntoView === 'function') sasaran.scrollIntoView({ block: 'nearest' })
}

/**
 * The form's default button, as the browser's implicit submission finds it: the first
 * submit button that belongs to the form (also one outside it through `form=`), or null.
 */
export function tombolKirim(form: HTMLFormElement): HTMLButtonElement | HTMLInputElement | null {
  for (const el of form.elements) {
    if (el instanceof HTMLButtonElement && el.type === 'submit') return el
    if (el instanceof HTMLInputElement && (el.type === 'submit' || el.type === 'image')) return el
  }
  return null
}

export function sasaranEnter(event: KeyboardEvent<HTMLElement>): SasaranEnter {
  const el = event.target as HTMLElement
  return {
    tag: el.tagName,
    jenis: el instanceof HTMLInputElement ? el.type : null,
    peran: el.getAttribute('role'),
    terbuka: el.getAttribute('aria-expanded') === 'true',
    dicegah: event.defaultPrevented,
    pengubah: event.shiftKey || event.ctrlKey || event.altKey || event.metaKey,
    // Safari ends a composition with a keydown that is not marked isComposing but has
    // keyCode 229.
    komposisi: event.nativeEvent.isComposing || event.keyCode === 229,
  }
}
